import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthProvider';
import versionControl from '../../../version-control.json';

interface NavItemProps {
  to: string;
  icon: string;
  label: string;
  badge?: string;
  badgeColor?: string;
  active: boolean;
  collapsed?: boolean;
}

const LojistaNavItem: React.FC<NavItemProps> = ({
  to,
  icon,
  label,
  badge,
  badgeColor = 'bg-purple-950/60 text-purple-300 border-purple-800/60',
  active,
  collapsed = false
}) => {
  return (
    <Link
      to={to}
      className={`flex items-center relative px-3 py-2 rounded-lg transition-colors duration-100 ${
        active
          ? 'bg-purple-950/40 text-purple-300 border-l-2 border-purple-500 font-bold'
          : 'text-slate-400 hover:bg-slate-800/60 hover:text-white'
      }`}
      title={collapsed ? label : undefined}
    >
      <div className={`flex items-center w-full ${collapsed ? 'justify-center' : 'space-x-3'}`}>
        <div className="relative flex items-center justify-center shrink-0">
          <i
            className={`fas ${icon} w-5 text-center text-sm ${
              active ? 'text-purple-400' : 'text-slate-400'
            }`}
          ></i>
        </div>
        {!collapsed && (
          <div className="flex-1 flex items-center justify-between min-w-0">
            <span className="text-xs truncate">{label}</span>
            {badge && (
              <span
                className={`ml-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 ${badgeColor}`}
              >
                {badge}
              </span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
};

const LojistaNavGroup: React.FC<{
  label: string;
  icon?: string;
  collapsed?: boolean;
  children: React.ReactNode;
}> = ({ label, icon, collapsed = false, children }) => (
  <div className="mb-4">
    {!collapsed && (
      <div className="px-3 text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5 flex items-center justify-between">
        <span className="flex items-center space-x-1.5">
          {icon && <i className={`fas ${icon} text-[9px] text-purple-400`}></i>}
          <span className="text-slate-300">{label}</span>
        </span>
        <span className="w-8 h-[1px] bg-slate-800"></span>
      </div>
    )}
    <div className="space-y-0.5">{children}</div>
  </div>
);

interface LojistaNavigationProps {
  collapsed?: boolean;
  onMobileClose?: () => void;
}

export const LojistaNavigation: React.FC<LojistaNavigationProps> = ({
  collapsed = false,
  onMobileClose
}) => {
  const { user } = useAuth();
  const location = useLocation();

  const isCurrent = (path: string, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const storeName = user?.store_name || user?.fullname || 'Minha Loja';
  const storeLogo = user?.store_logo || user?.avatar;

  return (
    <div className="flex flex-col h-full py-3 bg-slate-900 select-none">
      {/* Brand Header */}
      <div
        className={`px-4 py-3 flex items-center ${
          collapsed ? 'justify-center' : 'justify-between'
        } border-b border-slate-800/80 mb-2`}
      >
        <Link to="/" className="flex items-center space-x-2.5">
          <div className="bg-purple-600 px-2 py-1 rounded-lg text-white">
            <i className="fas fa-store text-sm"></i>
          </div>
          {!collapsed && (
            <div className="flex flex-col">
              <span className="text-sm font-bold tracking-tight text-white flex items-center gap-1.5">
                Cardumy
              </span>
            </div>
          )}
        </Link>

        {onMobileClose && (
          <button
            onClick={onMobileClose}
            className="md:hidden text-slate-500 hover:text-white p-1.5 rounded-lg hover:bg-slate-800 transition-colors"
            title="Fechar menu"
          >
            <i className="fas fa-xmark text-base"></i>
          </button>
        )}
      </div>

      {/* Navigation Scrollable Area */}
      <nav className="flex-1 px-3 overflow-y-auto space-y-4 custom-scrollbar">
        {/* Category: Visão Geral */}
        <LojistaNavGroup label="Visão Geral" icon="fa-chart-pie" collapsed={collapsed}>
          <LojistaNavItem
            to="/"
            icon="fa-gauge-high"
            label="Dashboard"
            active={location.pathname === '/' || location.pathname === '/dashboard' || location.pathname === '/inicio'}
            collapsed={collapsed}
          />
        </LojistaNavGroup>

        {/* Category: Vendas & Ponto de Venda (PDV) */}
        <LojistaNavGroup label="Vendas & Balcão" icon="fa-cash-register" collapsed={collapsed}>
          <LojistaNavItem
            to="/pedido-balcao"
            icon="fa-cash-register"
            label="Pedido no Balcão"
            active={isCurrent('/pedido-balcao')}
            collapsed={collapsed}
          />
          <LojistaNavItem
            to="/pedidos-recebidos"
            icon="fa-clipboard-check"
            label="Pedidos Recebidos"
            active={isCurrent('/pedidos-recebidos') || isCurrent('/pedidos')}
            collapsed={collapsed}
          />
          <LojistaNavItem
            to="/gerenciar-creditos"
            icon="fa-coins"
            label="Créditos de Clientes"
            active={
              isCurrent('/gerenciar-creditos') ||
              isCurrent('/creditos-clientes') ||
              isCurrent('/meus-creditos-loja')
            }
            collapsed={collapsed}
          />
        </LojistaNavGroup>

        {/* Category: Estoque & Catálogo */}
        <LojistaNavGroup label="Estoque & Catálogo" icon="fa-boxes-stacked" collapsed={collapsed}>
          <LojistaNavItem
            to="/gerenciar-estoque"
            icon="fa-boxes-stacked"
            label="Estoque da Loja"
            active={isCurrent('/gerenciar-estoque') || isCurrent('/meu-estoque')}
            collapsed={collapsed}
          />
          <LojistaNavItem
            to="/gerenciar-inventario"
            icon="fa-boxes-packing"
            label="Inventário Interno"
            active={
              isCurrent('/gerenciar-inventario') ||
              isCurrent('/meu-inventario') ||
              isCurrent('/inventario')
            }
            collapsed={collapsed}
          />
          <LojistaNavItem
            to="/produtos"
            icon="fa-bag-shopping"
            label="Catálogo Geral"
            active={isCurrent('/produtos')}
            collapsed={collapsed}
          />
          <LojistaNavItem
            to="/minha-loja/upload"
            icon="fa-cloud-arrow-up"
            label="Upload de Imagens"
            active={isCurrent('/minha-loja/upload')}
            collapsed={collapsed}
          />
        </LojistaNavGroup>

        {/* Category: Torneios & Eventos */}
        <LojistaNavGroup label="Eventos & Torneios" icon="fa-trophy" collapsed={collapsed}>
          <LojistaNavItem
            to="/novo-torneio"
            icon="fa-circle-plus"
            label="Criar Torneio"
            active={isCurrent('/novo-torneio')}
            collapsed={collapsed}
          />
          <LojistaNavItem
            to="/meus-torneios"
            icon="fa-trophy"
            label="Meus Torneios"
            active={isCurrent('/meus-torneios') && !isCurrent('/meus-torneios/importar')}
            collapsed={collapsed}
          />
          <LojistaNavItem
            to="/meus-torneios/importar"
            icon="fa-ranking-star"
            label="Classificação"
            active={isCurrent('/meus-torneios/importar') || isCurrent('/importar-resultados')}
            collapsed={collapsed}
          />
        </LojistaNavGroup>

        {/* Category: Configurações da Loja */}
        <LojistaNavGroup label="Configurações" icon="fa-gear" collapsed={collapsed}>
          <LojistaNavItem
            to="/minha-loja"
            icon="fa-store"
            label="Dados da Loja"
            active={location.pathname === '/minha-loja'}
            collapsed={collapsed}
          />
          <LojistaNavItem
            to="/minha-loja/horarios"
            icon="fa-clock"
            label="Horários de Atendimento"
            active={isCurrent('/minha-loja/horarios')}
            collapsed={collapsed}
          />
          <LojistaNavItem
            to="/minha-loja/agenda"
            icon="fa-calendar-days"
            label="Agenda Semanal"
            active={isCurrent('/minha-loja/agenda')}
            collapsed={collapsed}
          />
          <LojistaNavItem
            to="/minha-loja/jogos"
            icon="fa-gamepad"
            label="Jogos Suportados"
            active={isCurrent('/minha-loja/jogos')}
            collapsed={collapsed}
          />
          <LojistaNavItem
            to="/minha-loja/frete-pagamento"
            icon="fa-truck-fast"
            label="Frete & Pagamento"
            active={isCurrent('/minha-loja/frete-pagamento') || isCurrent('/minha-loja/envio-pagamento')}
            collapsed={collapsed}
          />
          <LojistaNavItem
            to="/minha-loja/mercadopago"
            icon="fa-wallet"
            label="Mercado Pago"
            active={isCurrent('/minha-loja/mercadopago')}
            collapsed={collapsed}
          />
        </LojistaNavGroup>

        {/* Category: Outros */}
        <LojistaNavGroup label="Outros" icon="fa-ellipsis" collapsed={collapsed}>
          <LojistaNavItem
            to="/suporte"
            icon="fa-circle-question"
            label="Suporte"
            active={isCurrent('/suporte')}
            collapsed={collapsed}
          />
          <LojistaNavItem
            to="/termos-do-lojista"
            icon="fa-file-lines"
            label="Termos do Lojista"
            active={isCurrent('/termos-do-lojista') || isCurrent('/minha-loja/termos')}
            collapsed={collapsed}
          />
        </LojistaNavGroup>
      </nav>

      {/* Footer / Versão da Compilação */}
      <div
        className={`border-t border-slate-800/80 mt-auto flex items-center justify-center text-slate-500 select-none ${
          collapsed ? 'py-2.5 px-1' : 'py-2.5 px-3'
        }`}
      >
        {collapsed ? (
          <span
            title={`Lojista: ${versionControl.lojista}`}
            className="text-[9px] font-mono text-slate-400 hover:text-purple-300 transition-colors cursor-default tracking-tighter"
          >
            {versionControl.lojista}
          </span>
        ) : (
          <div className="flex items-center justify-between w-full px-1 text-[11px] font-mono">
            <span className="text-slate-500 text-[10px] font-sans font-bold uppercase tracking-wider flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse"></span>
              Versão
            </span>
            <span className="text-purple-400 font-medium bg-purple-950/40 px-2 py-0.5 rounded border border-purple-800/40">
              {versionControl.lojista}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
